"use client";

import { useEffect } from "react";

import { useInstantAssistantController } from "../useInstantAssistantController";
import { useInstantAssistantStore } from "../useInstantAssistantStore";

export function AssistantHotkeyHandler() {
  const featureEnabled = useInstantAssistantStore((state) => state.featureEnabled);
  const voiceFeatureEnabled = useInstantAssistantStore((state) => state.voiceFeatureEnabled);
  const voiceConnectionState = useInstantAssistantStore((state) => state.voiceConnectionState);
  const isPanelOpen = useInstantAssistantStore((state) => state.isPanelOpen);
  const actions = useInstantAssistantStore((state) => state.actions);
  const { startVoiceSession, endVoiceSession } = useInstantAssistantController();

  useEffect(() => {
    if (!featureEnabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.key === "Escape" && event.shiftKey && isPanelOpen) {
        actions.requestFocusRelease();
        event.preventDefault();
        return;
      }

      if (event.key === "Escape" && isPanelOpen) {
        actions.closePanel();
        event.preventDefault();
        return;
      }

      // No wake-up shortcut here — wake-up lives in AssistantOverlayProvider
      // (Cmd/Ctrl+K and double-tap Shift). This handler only manages the
      // legacy panel's Esc / Shift+Esc dismissal.
      return;
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    featureEnabled,
    voiceFeatureEnabled,
    voiceConnectionState,
    isPanelOpen,
    actions,
    startVoiceSession,
    endVoiceSession,
  ]);

  return null;
}

function shouldIgnoreTarget(target: Element | null) {
  if (!target) {
    return false;
  }

  const interactiveSelectors = ["input", "textarea", "select", "button", "[contenteditable=true]"];
  if (target.matches(interactiveSelectors.join(","))) {
    return true;
  }

  const editorSelectors = [".monaco-editor", ".cm-editor", ".cm-content", "[data-monaco-editor]"];
  if (editorSelectors.some((selector) => target.closest(selector))) {
    return true;
  }

  if (target.hasAttribute("data-scirix-no-shortcut")) {
    return true;
  }

  const parentWithOptOut = target.closest("[data-scirix-no-shortcut]");
  if (parentWithOptOut) {
    return true;
  }

  return false;
}
